/**
 * Board renderer: the hex galaxy itself.
 *
 * Draw order is fixed and matters — see `LAYER` in palette.ts. The passes are separated
 * (all plates, then all detail, then all pieces) rather than doing everything per hex,
 * because batching by pass means far fewer canvas state changes: one `shadowBlur` set-up
 * for every glow instead of 61.
 *
 * This module never touches game state. It reads a `GameState` and a `BoardView` of what
 * the UI wants highlighted, and draws. All interaction lives in the UI controller.
 */

import { HEX_GAP, HEX_SIZE, REGIONS, SYSTEM_CLASSES } from '../game/constants';
import { parseKey, toPixel } from '../game/hex';
import { fleetsAt, isBlocked } from '../game/queries';
import type { GameState, HexKey, PlayerId, StarSystem } from '../game/types';
import { Camera } from './camera';
import {
  INK, LAYER, ease, font, RESOURCE_INK, seatColor, systemPaint, TYPE, withAlpha,
} from './palette';
import {
  drawDevelopment, drawFleet, drawHabitability, drawHazard, drawMegastructure, drawRelic,
  drawSeatMarker, drawYieldPips, hexPath, planetSprite, unknownSprite,
} from './sprites';
import { Starfield } from './starfield';
import { ParticleSystem } from './particles';

/** What the UI wants emphasised this frame. */
export interface BoardView {
  /** Hexes that are legal targets for the pending action. */
  targets: Set<HexKey>;
  /** Hexes the selected fleet can reach, mapped to jump count. */
  reachable: Map<HexKey, number>;
  /** Preview jump chain for a pending move. */
  path: HexKey[];
  /** One cursor per active seat — several at once during sealed-bid phases. */
  cursors: { seat: PlayerId; id: HexKey; dim?: boolean }[];
  /** System whose detail card is open, drawn with a stronger ring. */
  inspect: HexKey | null;
  /** Fleet currently selected for a move, so it can be drawn lifted. */
  selectedFleet: string | null;
  /** Systems to flash — e.g. blocked by an ion storm this round. */
  flash: Set<HexKey>;
  /** Dim everything except `targets`, used while a targeting action is armed. */
  dimNonTargets: boolean;
}

export function emptyBoardView(): BoardView {
  return {
    targets: new Set(),
    reachable: new Map(),
    path: [],
    cursors: [],
    inspect: null,
    selectedFleet: null,
    flash: new Set(),
    dimNonTargets: false,
  };
}

export class BoardRenderer {
  readonly starfield: Starfield;
  readonly particles = new ParticleSystem();

  /** Wall-clock ms, advanced by the game loop; drives all idle animation. */
  private time = 0;

  constructor(seed: number) {
    this.starfield = new Starfield(seed);
  }

  resize(width: number, height: number, dpr: number): void {
    this.starfield.resize(width, height, dpr);
  }

  update(dtMs: number): void {
    this.time += dtMs;
    this.particles.update(dtMs);
  }

  /** Convenience for the notice bus: world position of a system, in screen pixels. */
  screenOf(camera: Camera, id: HexKey): { x: number; y: number } {
    return camera.hexKeyToScreen(id);
  }

  draw(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    camera: Camera,
    view: BoardView,
  ): void {
    const { width, height } = camera.viewport;

    ctx.save();
    ctx.clearRect(0, 0, width, height);

    // LAYER.starfield
    this.starfield.draw(ctx, camera.x, camera.y, camera.zoom, this.time);

    const systems = Object.values(state.systems);
    const visible = systems.filter((s) => camera.isVisible(s.coord));

    this.drawRegionBands(ctx, camera);                       // LAYER.regionBands
    this.drawPlates(ctx, state, camera, view, visible);      // LAYER.hexPlates
    this.drawBodies(ctx, camera, view, visible);             // LAYER.hexDetail
    this.drawSystemDetail(ctx, state, camera, view, visible);
    this.drawRoutes(ctx, camera, view);                      // LAYER.routes
    this.drawPieces(ctx, state, camera, view, visible);      // LAYER.fleets + markers
    this.drawCursors(ctx, camera, view);                     // LAYER.cursor

    ctx.restore();

    // LAYER.particles — drawn outside the board transform so text stays upright.
    this.particles.draw(ctx);
  }

  /* -------------------------------------------------------------- region bands */

  /**
   * Faint concentric rings marking Core / Mid Rim / Outer Rim.
   *
   * Region majorities are worth up to 5 VP, so players need to see the boundaries at a
   * glance without the map turning into a chart.
   */
  private drawRegionBands(ctx: CanvasRenderingContext2D, camera: Camera): void {
    const centre = camera.worldToScreen({ x: 0, y: 0 });
    const hexH = HEX_SIZE * 1.5 * camera.zoom;

    for (const region of ['core', 'midRim', 'outerRim'] as const) {
      const def = REGIONS[region];
      const outer = (def.rings[1] + 0.55) * hexH * 1.16;
      const inner = (def.rings[0] - 0.45) * hexH * 1.16;

      ctx.save();
      ctx.beginPath();
      ctx.arc(centre.x, centre.y, Math.max(0, outer), 0, Math.PI * 2);
      if (inner > 0) {
        ctx.arc(centre.x, centre.y, inner, 0, Math.PI * 2, true);
      }
      ctx.fillStyle = withAlpha(def.tint, 0.035);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(centre.x, centre.y, Math.max(0, outer), 0, Math.PI * 2);
      ctx.strokeStyle = withAlpha(def.tint, 0.14);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([7, 11]);
      ctx.stroke();
      ctx.restore();
    }
  }

  /* -------------------------------------------------------------------- plates */

  /** The hex tiles: ownership tint, survey state, target highlight. */
  private drawPlates(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    camera: Camera,
    view: BoardView,
    visible: StarSystem[],
  ): void {
    const size = camera.hexScreenSize - HEX_GAP * camera.zoom;
    const pulse = ease.pulse((this.time % 1600) / 1600);

    for (const system of visible) {
      const p = camera.hexToScreen(system.coord);
      const paint = systemPaint(system.cls);
      const isTarget = view.targets.has(system.id);
      const dimmed = view.dimNonTargets && !isTarget;

      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.34;

      // Base plate.
      hexPath(ctx, p.x, p.y, size);
      if (!system.surveyed) {
        ctx.fillStyle = 'rgba(14, 17, 30, 0.72)';
      } else if (system.owner !== null) {
        // Owner tint reads first — who owns what is the single most-asked question.
        const grad = ctx.createLinearGradient(p.x, p.y - size, p.x, p.y + size);
        grad.addColorStop(0, withAlpha(seatColor(system.owner), 0.34));
        grad.addColorStop(1, withAlpha(paint.plate, 0.94));
        ctx.fillStyle = grad;
      } else {
        ctx.fillStyle = withAlpha(paint.plate, 0.82);
      }
      ctx.fill();

      // Rim.
      ctx.lineWidth = system.owner !== null ? 3 : 1.4;
      ctx.strokeStyle = system.owner !== null
        ? withAlpha(seatColor(system.owner), 0.92)
        : system.surveyed ? withAlpha(paint.rim, 0.7) : INK.edge;
      ctx.stroke();

      // Blocked systems (ion storm / solar flare) get a hatched veil, because "why is
      // this producing nothing" needs an on-board answer.
      if (isBlocked(system, state.round)) {
        this.drawBlockedVeil(ctx, p.x, p.y, size);
      }

      // Target highlight.
      if (isTarget) {
        hexPath(ctx, p.x, p.y, size * (0.97 + pulse * 0.03));
        ctx.strokeStyle = withAlpha(INK.good, 0.55 + pulse * 0.4);
        ctx.lineWidth = 3.5;
        ctx.stroke();
      }

      // Reachable-but-not-target: a softer fill so move range is obvious.
      const jumps = view.reachable.get(system.id);
      if (jumps !== undefined && !isTarget) {
        hexPath(ctx, p.x, p.y, size);
        ctx.fillStyle = withAlpha(INK.info, 0.1);
        ctx.fill();
      }

      if (view.flash.has(system.id)) {
        hexPath(ctx, p.x, p.y, size);
        ctx.fillStyle = withAlpha(INK.warn, 0.14 + pulse * 0.16);
        ctx.fill();
      }

      ctx.restore();
    }
  }

  /** Diagonal hatch showing a system is producing nothing this round. */
  private drawBlockedVeil(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
  ): void {
    ctx.save();
    hexPath(ctx, cx, cy, size);
    ctx.clip();
    ctx.strokeStyle = withAlpha(INK.warn, 0.2);
    ctx.lineWidth = 2;
    const step = 9;
    for (let offset = -size; offset <= size * 2; offset += step) {
      ctx.beginPath();
      ctx.moveTo(cx - size + offset, cy - size);
      ctx.lineTo(cx - size + offset - size * 2, cy + size);
      ctx.stroke();
    }
    ctx.restore();
  }

  /* -------------------------------------------------------------------- bodies */

  /** Planet art, blitted from the sprite cache. */
  private drawBodies(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    view: BoardView,
    visible: StarSystem[],
  ): void {
    const size = camera.hexScreenSize;
    // Quantise the sprite size so the cache does not thrash while the camera eases.
    const diameter = Math.max(8, Math.round((size * 1.06) / 4) * 4);

    for (const system of visible) {
      if (system.cls === 'deepSpace' && system.surveyed) continue;

      const p = camera.hexToScreen(system.coord);
      const dimmed = view.dimNonTargets && !view.targets.has(system.id);

      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.34;

      const sprite = system.surveyed
        ? planetSprite(system, diameter)
        : unknownSprite(system.artSeed, diameter);

      ctx.drawImage(sprite, p.x - diameter / 2, p.y - diameter / 2, diameter, diameter);
      ctx.restore();
    }
  }

  /* ------------------------------------------------------------ system detail */

  /**
   * Yield pips, hazard chevrons, habitability dots, relic sigils.
   *
   * Skipped entirely below a zoom threshold: at a fitted view of the whole board these
   * would be unreadable clutter, and the inspect card covers the detail case.
   */
  private drawSystemDetail(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    camera: Camera,
    view: BoardView,
    visible: StarSystem[],
  ): void {
    const size = camera.hexScreenSize;
    if (size < 26) return;

    const showFull = size >= 40;

    for (const system of visible) {
      if (!system.surveyed || system.cls === 'deepSpace') continue;
      const p = camera.hexToScreen(system.coord);
      const dimmed = view.dimNonTargets && !view.targets.has(system.id);

      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.3;

      if (system.relic && !system.relicTaken) {
        drawRelic(ctx, p.x + size * 0.52, p.y - size * 0.58, size * 0.3, system.relic, false);
      }

      if (showFull) {
        drawYieldPips(ctx, p.x, p.y + size * 0.66, size * 0.2, system.yield);
        if (system.hazard > 0) {
          drawHazard(ctx, p.x - size * 0.6, p.y - size * 0.5, size * 0.16, system.hazard);
        }
        drawHabitability(ctx, p.x + size * 0.02, p.y - size * 0.74, size * 0.11, system.habitability);
      }

      if (system.megastructure) {
        drawMegastructure(
          ctx, p.x, p.y, size * 0.9, system.megastructure,
          system.owner ?? 0, this.time,
        );
      }

      ctx.restore();
    }

    // Round-blocked count is useful context; draw it once, not per hex.
    void state;
  }

  /* -------------------------------------------------------------------- routes */

  /** The pending move's jump chain, as a dashed animated ribbon. */
  private drawRoutes(ctx: CanvasRenderingContext2D, camera: Camera, view: BoardView): void {
    if (view.path.length === 0) return;

    const points = view.path.map((id) => camera.hexKeyToScreen(id));
    ctx.save();
    ctx.strokeStyle = withAlpha(INK.info, 0.85);
    ctx.lineWidth = Math.max(2.5, camera.zoom * 3.2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([12, 9]);
    // Marching dashes read as direction of travel without an arrowhead per segment.
    ctx.lineDashOffset = -(this.time / 26) % 21;
    ctx.shadowColor = withAlpha(INK.info, 0.6);
    ctx.shadowBlur = 12;

    ctx.beginPath();
    ctx.moveTo(points[0]!.x, points[0]!.y);
    for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
    ctx.stroke();

    // Destination pip.
    const last = points[points.length - 1]!;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(last.x, last.y, camera.hexScreenSize * 0.2, 0, Math.PI * 2);
    ctx.fillStyle = withAlpha(INK.info, 0.3);
    ctx.fill();
    ctx.strokeStyle = INK.info;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.restore();
  }

  /* -------------------------------------------------------------------- pieces */

  /** Developments and fleets. */
  private drawPieces(
    ctx: CanvasRenderingContext2D,
    state: GameState,
    camera: Camera,
    view: BoardView,
    visible: StarSystem[],
  ): void {
    const size = camera.hexScreenSize;

    for (const system of visible) {
      const p = camera.hexToScreen(system.coord);
      const dimmed = view.dimNonTargets && !view.targets.has(system.id);

      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.34;

      if (system.owner !== null && system.development !== 'none') {
        drawDevelopment(ctx, p.x, p.y, size * 0.42, system.owner, system.development);
        // Seat shape marker beside the structure — colour-blind redundancy.
        drawSeatMarker(ctx, p.x - size * 0.5, p.y + size * 0.5, size * 0.14, system.owner, true);
      }

      // Fleets fan out around the hex so a contested system reads as contested.
      const here = fleetsAt(state, system.id);
      if (here.length > 0) {
        this.drawFleetCluster(ctx, p.x, p.y, size, here, view);
      }
      ctx.restore();
    }
  }

  /**
   * Fleets in one system, arranged on an arc.
   *
   * Grouped by seat so four fleets from two players read as "two sides", not "four
   * ships" — that distinction is the whole point during the Contest phase.
   */
  private drawFleetCluster(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    fleets: { id: string; owner: PlayerId }[],
    view: BoardView,
  ): void {
    const bySeat = new Map<PlayerId, string[]>();
    for (const fleet of fleets) {
      const list = bySeat.get(fleet.owner) ?? [];
      list.push(fleet.id);
      bySeat.set(fleet.owner, list);
    }

    const seats = [...bySeat.keys()].sort((a, b) => a - b);
    const contested = seats.length >= 2;
    const radius = size * (contested ? 0.62 : 0.5);

    seats.forEach((seat, index) => {
      const ids = bySeat.get(seat)!;
      // Spread seats around the hex; a lone seat sits at the bottom-right.
      const angle = contested
        ? (-Math.PI / 2) + (index / seats.length) * Math.PI * 2
        : Math.PI * 0.28;

      const fx = cx + Math.cos(angle) * radius;
      const fy = cy + Math.sin(angle) * radius;
      const lifted = ids.some((id) => id === view.selectedFleet);

      drawFleet(ctx, fx, fy - (lifted ? size * 0.12 : 0), size * 0.3, seat, {
        count: ids.length,
        facing: angle,
        ghost: false,
      });

      if (lifted) {
        ctx.save();
        ctx.strokeStyle = withAlpha(seatColor(seat), 0.9);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(fx, fy + size * 0.14, size * 0.22, size * 0.07, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    });

    if (contested) {
      // A clash ring: unmissable, because a missed contest is a lost round.
      const pulse = ease.pulse((this.time % 900) / 900);
      ctx.save();
      ctx.strokeStyle = withAlpha(INK.bad, 0.5 + pulse * 0.45);
      ctx.lineWidth = 2.5 + pulse * 1.5;
      ctx.setLineDash([6, 6]);
      ctx.lineDashOffset = (this.time / 40) % 12;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 0.78, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  /* ------------------------------------------------------------------- cursors */

  /**
   * Seat cursors. Several can be live at once (the sealed-bid phases), so each is drawn
   * with its seat colour, its seat shape, and a slight rotation offset so two cursors on
   * the same hex are still both visible.
   */
  private drawCursors(ctx: CanvasRenderingContext2D, camera: Camera, view: BoardView): void {
    const size = camera.hexScreenSize;
    const pulse = ease.pulse((this.time % 1100) / 1100);

    if (view.inspect) {
      const p = camera.hexKeyToScreen(view.inspect);
      ctx.save();
      hexPath(ctx, p.x, p.y, size * 0.99);
      ctx.strokeStyle = withAlpha(INK.text, 0.55);
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 6]);
      ctx.stroke();
      ctx.restore();
    }

    view.cursors.forEach((cursor, index) => {
      const p = camera.hexKeyToScreen(cursor.id);
      const color = seatColor(cursor.seat);
      const wobble = index * 3.5;

      ctx.save();
      if (cursor.dim) ctx.globalAlpha = 0.45;

      // Bracket corners rather than a full outline: it frames the hex without hiding
      // the art underneath.
      const r = size * (0.94 + pulse * 0.05) + wobble;
      ctx.strokeStyle = color;
      ctx.lineWidth = 3.5;
      ctx.lineCap = 'round';
      ctx.shadowColor = withAlpha(color, 0.85);
      ctx.shadowBlur = 14;

      for (let corner = 0; corner < 6; corner++) {
        const a0 = (Math.PI / 180) * (60 * corner - 90);
        const a1 = (Math.PI / 180) * (60 * corner - 90 + 22);
        const a2 = (Math.PI / 180) * (60 * corner - 90 - 22);
        const px = p.x + r * Math.cos(a0);
        const py = p.y + r * Math.sin(a0);
        ctx.beginPath();
        ctx.moveTo(p.x + r * Math.cos(a1), p.y + r * Math.sin(a1));
        ctx.lineTo(px, py);
        ctx.lineTo(p.x + r * Math.cos(a2), p.y + r * Math.sin(a2));
        ctx.stroke();
      }

      drawSeatMarker(ctx, p.x, p.y - r - size * 0.16, size * 0.15, cursor.seat, true);
      ctx.restore();
    });
  }

  /* ----------------------------------------------------------------- utilities */

  /**
   * Screen position for a floating label over a system, nudged upward so it clears the
   * planet art. Used by the notice bus for "+3 Ore" style popups.
   */
  labelAnchor(camera: Camera, id: HexKey): { x: number; y: number } {
    const p = camera.hexKeyToScreen(id);
    return { x: p.x, y: p.y - camera.hexScreenSize * 0.55 };
  }

  /** Emit the particle burst appropriate to a system-anchored notice. */
  celebrate(camera: Camera, id: HexKey, kind: 'claim' | 'upgrade' | 'relic' | 'contest', seat: PlayerId): void {
    const p = camera.hexKeyToScreen(id);
    switch (kind) {
      case 'claim': this.particles.burstClaim(p.x, p.y, seat); break;
      case 'upgrade': this.particles.burstUpgrade(p.x, p.y, seat); break;
      case 'relic': this.particles.burstRelic(p.x, p.y); break;
      case 'contest': this.particles.burstContest(p.x, p.y); break;
    }
  }

  /** Float a "+N Ore" style label at a system. */
  floatGain(camera: Camera, id: HexKey, text: string, resource: keyof typeof RESOURCE_INK): void {
    const anchor = this.labelAnchor(camera, id);
    this.particles.floatText(anchor.x, anchor.y, text, RESOURCE_INK[resource]);
  }

  /** Trail for a fleet jump, drawn in screen space between two hexes. */
  jump(camera: Camera, from: HexKey, to: HexKey, seat: PlayerId): void {
    const a = camera.hexKeyToScreen(from);
    const b = camera.hexKeyToScreen(to);
    this.particles.jumpTrail(a.x, a.y, b.x, b.y, seat);
  }

  /**
   * Text label drawn directly on the board — only used for the region names, which sit
   * behind everything at low opacity as orientation cues.
   */
  drawRegionLabels(ctx: CanvasRenderingContext2D, camera: Camera): void {
    if (camera.zoom < 0.7) return;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = font(TYPE.label, 700);

    for (const region of ['core', 'midRim', 'outerRim'] as const) {
      const def = REGIONS[region];
      // Place the label at the top of the region's outer ring.
      const ringIndex = def.rings[1];
      const world = toPixel({ q: 0, r: -ringIndex }, HEX_SIZE);
      const p = camera.worldToScreen({ x: world.x, y: world.y - HEX_SIZE * 0.9 });
      ctx.fillStyle = withAlpha(def.tint, 0.35);
      ctx.fillText(def.name.toUpperCase(), p.x, p.y);
    }
    ctx.restore();
  }

  /** Human-readable one-liner for a system, used by the inspect card and the log. */
  static describe(system: StarSystem): string {
    if (!system.surveyed) return 'Uncharted';
    const def = SYSTEM_CLASSES[system.cls];
    if (system.cls === 'deepSpace') return 'Deep Space — nothing to claim';
    return `${def.name} · hab ${system.habitability} · hazard ${system.hazard}`;
  }
}

/** Cheap hit-test from a screen point to a system id, or null. */
export function pickSystem(
  state: GameState,
  camera: Camera,
  screen: { x: number; y: number },
): HexKey | null {
  const coord = camera.screenToHex(screen);
  const id: HexKey = `${coord.q},${coord.r}`;
  return state.systems[id] ? id : null;
}

/** Nearest system to a hex key that actually exists, for clamping cursor movement. */
export function clampToBoard(state: GameState, id: HexKey, fallback: HexKey): HexKey {
  return state.systems[id] ? id : fallback;
}

/** Neighbour in a direction if it exists on the board, else the original. */
export function stepCursor(state: GameState, from: HexKey, direction: number): HexKey {
  const coord = parseKey(from);
  const dirs = [
    { q: 1, r: 0 }, { q: 1, r: -1 }, { q: 0, r: -1 },
    { q: -1, r: 0 }, { q: -1, r: 1 }, { q: 0, r: 1 },
  ];
  const d = dirs[((direction % 6) + 6) % 6]!;
  const next: HexKey = `${coord.q + d.q},${coord.r + d.r}`;
  return state.systems[next] ? next : from;
}

export { LAYER };
